import { useMemo, type JSX, type HTMLProps, type ComponentProps } from 'react';

import type { ExtraProps } from 'react-markdown';
import type { SyntaxHighlighterProps } from 'react-syntax-highlighter';

import { Code } from '../Code';
import { Syntax } from './Syntax';
import { groupLines } from './utils';
import { Description } from './Description';

type TMetaProps = Omit<SyntaxHighlighterProps, 'children'>;

interface ICodeBlockProps extends ComponentProps<'code'>, ExtraProps {
  inline?: boolean;
}

export function CodeBlock({
  ref,
  node,
  inline,
  children,
  className,
  ...props
}: ICodeBlockProps): JSX.Element {
  const match = /language-(\w+)/.exec(className || '');
  const { data } = node || {};
  const { meta } = (data || {}) as {
    meta?: string;
  };

  const { filename, ...metaProps } = useMemo<TMetaProps>(() => {
    if (!meta) {
      return {};
    }

    let result: TMetaProps = {};
    const metaData = meta.split(';');

    metaData.forEach((datumn) => {
      const [key, value] = datumn.split('=');

      switch (key) {
        case 'showLineNumbers': {
          result.showLineNumbers = true;

          break;
        }
        case 'focus': {
          if (!value) {
            break;
          }

          const lines = groupLines(value);

          result = {
            ...result,
            showLineNumbers: true,
            lineProps: (lineNumber: number) => {
              const style: HTMLProps<HTMLElement>['style'] = {
                opacity: 0.35,
              };

              if (lines[lineNumber]) {
                style.opacity = 1;
              }

              return { style };
            },
          };

          break;
        }
        case 'filename': {
          result.filename = value;
          break;
        }
        case 'edit': {
          if (!value) {
            break;
          }

          const [plusLines, minusLines] = value
            .split('|')
            .reduce<[Record<number, true>, Record<number, true>]>(
              (result, line) => {
                if (line.match(/^\+/)) {
                  result[0] = groupLines(line.replace(/^\+/, ''));
                } else if (line.match(/^-/)) {
                  result[1] = groupLines(line.replace(/^-/, ''));
                }

                return result;
              },
              [{}, {}],
            );

          result = {
            ...result,
            showLineNumbers: true,
            lineProps: (lineNumber: number) => {
              const style: HTMLProps<HTMLElement>['style'] = {
                opacity: 0.6,
              };

              if (plusLines[lineNumber]) {
                style.opacity = 1;
                style.width = '100%';
                style.display = 'block';
                style.backgroundColor = 'rgba(46, 160, 67, 0.15)';
              }

              if (minusLines[lineNumber]) {
                style.opacity = 1;
                style.width = '100%';
                style.display = 'block';
                style.backgroundColor = 'rgba(248, 81, 73, 0.1)';
              }

              return {
                style,
              };
            },
          };

          break;
        }
      }
    });

    return result;
  }, [meta]);

  if (!inline && match) {
    const lang = match[1];

    if (lang === 'description') {
      return <Description filename={filename} content={children as string} />;
    }

    return (
      <Syntax
        ref={ref}
        language={lang}
        meta={metaProps}
        filename={filename}
        {...props}
      >
        {children}
      </Syntax>
    );
  }

  return (
    <Code className={className} {...props}>
      {children}
    </Code>
  );
}
